import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import React, { memo, startTransition, useEffect, useMemo, useRef, useState } from 'react';

import useAppSelector from '../../util/hooks/useAppSelector';
import ListingCard from '../BasicUI/ListingCard/ListingCard';
import ListingCardSkeleton from '../BasicUI/ListingCard/ListingCardSkeleton';
import NoResultsState from '../CallToActions/EmptyStates/NoResults/NoResultsState';
import PaginationLinks from '../Links/PaginationLinks/PaginationLinks';
// Side-effect import: this module reads the SearchPage redux slice, which is injected
// lazily by the duck. Importing it keeps the reducer in whatever chunk this module lands in.
import '../../containers/SearchPage/SearchPage.duck';

const BATCH_SIZE = 24;

// content-visibility: auto tells the browser to skip layout/paint for offscreen items entirely
const listingGridItemSx = {
  contentVisibility: 'auto',
  containIntrinsicSize: 'auto 320px',
};

const SearchResultsPanel = ({
  listings = [],
  pagination = null,
  search = null,
  paginationPageName = 'SearchPage',
  paginationPathParams = {},
  amountListings = null,
  setActiveListing = null,
  onMapIconClick = null,
  shouldShowSearchMap = false,
  urlCategory = '',
}) => {
  const searchInProgress = useAppSelector((state) => state.SearchPage.searchInProgress);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        pageName={paginationPageName}
        pagePathParams={paginationPathParams}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const noResultsState = <NoResultsState onMapIconClick={onMapIconClick} />;

  const slicedListings = useMemo(
    () => (typeof amountListings === 'number' ? listings.slice(0, amountListings) : listings),
    [amountListings, listings],
  );

  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [slicedListings]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTransition(() => {
            setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, slicedListings.length));
          });
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleCount, slicedListings.length]);

  const visibleListings = slicedListings.slice(0, visibleCount);

  return (
    <Box>
      <Grid container>
        <Grid columnSpacing={2} container rowSpacing={2} size={12}>
          {!searchInProgress
            ? visibleListings.map((l) => (
              <Grid
                key={l.id.uuid}
                size={{
                  lg: shouldShowSearchMap ? 6 : 3,
                  md: 6,
                  sm: shouldShowSearchMap ? 12 : 6,
                  xl: shouldShowSearchMap ? 4 : 3,
                  xs: 12,
                }}
                sx={listingGridItemSx}
              >
                <ListingCard
                  listing={l}
                  setActiveListing={setActiveListing}
                  urlCategory={urlCategory}
                />
              </Grid>
            ))
            : Array.from({ length: 9 }).map((_, i) => (
              <Grid
                key={i}
                size={{ lg: shouldShowSearchMap ? 6 : 3, md: 6, sm: 12, xl: shouldShowSearchMap ? 4 : 3, xs: 12 }}
              >
                <ListingCardSkeleton />
              </Grid>
            ))}
          {slicedListings.length === 0 && !searchInProgress ? noResultsState : null}
        </Grid>
        {visibleCount < slicedListings.length && !searchInProgress ? (
          <div ref={sentinelRef} style={{ height: 1 }} />
        ) : null}
        {slicedListings.length !== 0 ? (
          <Grid
            mb={{
              xs: 12,
              lg: 2,
            }}
            size={12}
          >
            {paginationLinks}
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
};

export default memo(SearchResultsPanel);
